import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import classes from './giveAccess.module.scss';
import InputField from '../../../../_common/Form/TextFields/InputField';
import Button from '../../../../_common/Form/Buttons/Button';
import { getPhysiciansRequest, getPhysicianUsersRequest } from '../../../../../store/actions/physicians';
import accountHelpers from '../../../../../utils/accountHelpers';
import Pagination from '../../../../_common/Pagination/Pagination';
import api from '../../../../../api/api';
import utils from '../../../../../utils/utils';
import Loader from '../../../../_common/Loader/Loader';
import { ReactComponent as DeniedIcon } from '../../../../../assets/icons/denied.svg';
import { getHealthRequest } from '../../../../../store/actions/healths';

let firstLoad = true;
let timeout;

const GiveAccess = ({ group, health }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const {
    physiciansList, physicianUsers, physicianUsersTotalPage,
  } = useSelector((store) => store.physicians);
  const healthList = useSelector((store) => store.healths.healthList);

  const { physicianId, groupName, healthId } = useParams();

  const initialData = useCallback(() => {
    const data = {
      page: 1,
      limit: 15,
      name: '',
    };

    if (health) {
      data.healthId = healthId;
      data.access = true;
      data.role = 'Health';
    } else {
      data.role = 'Physician';
    }

    return data;
  }, []);

  const [requestData, setRequestData] = useState(initialData);

  const [buttonLoad, setButtonLoad] = useState({
    id: '',
    load: false,
  });

  const [loading, setLoading] = useState(false);

  const { page, limit, name } = requestData;

  const dataRequest = group
    ? { group: groupName, userId: accountHelpers.getUser().id }
    : health
      ? { healthId, userId: accountHelpers.getUser().id }
      : { physicianId, userId: accountHelpers.getUser().id };

  useEffect(() => {
    (async () => {
      const data = utils.deleteEmptyKeys(requestData);

      if (!health) {
        await Promise.all([
          dispatch(getPhysicianUsersRequest(data)),
          dispatch(getPhysiciansRequest(dataRequest)),
        ]);
      } else {
        await Promise.all([
          dispatch(getPhysicianUsersRequest(data)),
          dispatch(getHealthRequest(dataRequest)),
        ]);
      }

      firstLoad = false;
    })();
  }, []);

  const giveAccess = async (userId, type) => {
    const data = utils.deleteEmptyKeys(requestData);

    setButtonLoad({ id: userId, load: true });

    try {
      if (type === 'add') {
        if (group) {
          await Promise.all(
            physiciansList.map((p) => api.addPhysicianAccess({ physicianId: p.id, userId })),
          );
        } else if (health) {
          await api.sendHealthGroup({ groupId: healthId, id: userId });
        } else {
          await api.addPhysicianAccess({ physicianId: physiciansList[0].id, userId });
        }

        toast.success('Access successfully added');
      } else {
        if (group) {
          await Promise.all(
            physiciansList.map((p) => api.deletePhysicianAccess({ physicianId: p.id, userId })),
          );
        } else if (health) {
          await api.deleteHealthGroup({ groupId: healthId, id: userId });
        } else {
          await api.deletePhysicianAccess({ physicianId: physiciansList[0].id, userId });
        }

        toast.success('Access successfully deleted');
      }
    } catch (e) {
      toast.error('Something went wrong');
    }

    await dispatch(getPhysicianUsersRequest(data));

    setButtonLoad({ id: userId, load: false });
  };

  const onChangePageData = async (path, count) => {
    setLoading(true);

    const data = utils.deleteEmptyKeys({ ...requestData, [path]: count });

    if (path === 'limit') {
      data.page = 1;
    }

    await dispatch(getPhysicianUsersRequest(data));

    setRequestData(data);
    setLoading(false);
  };

  const onChangeSearch = (value) => {
    setLoading(true);

    setRequestData((prev) => ({ ...prev, name: value.trim(), page: 1 }));

    clearTimeout(timeout);

    const newQuery = utils.deleteEmptyKeys({ ...requestData, name: value.trim(), page: 1 });

    timeout = setTimeout(async () => {
      await dispatch(getPhysicianUsersRequest(newQuery));

      setLoading(false);
    }, 400);
  };

  const clearInput = async () => {
    setLoading(true);
    const newQuery = utils.deleteEmptyKeys({ ...requestData, name: '', page: 1 });

    setRequestData((prev) => ({ ...prev, name: '', page: 1 }));

    await dispatch(getPhysicianUsersRequest(newQuery));

    setLoading(false);
  };

  const groupClick = (nameGroup) => {
    navigate(`/physicians/groups/${nameGroup}/access`);
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <div className={classes.title}>
          <div>
            <h2>
              {group
                ? 'Group Access'
                : health
                  ? healthList[0]?.name
                  : `${physiciansList[0]?.firstName} ${physiciansList[0]?.lastName}`}
            </h2>

            {!health && (
            <p
              onClick={() => groupClick(physiciansList[0]?.group)}
              style={!group
                ? { cursor: 'pointer' }
                : { pointerEvents: 'none' }}
            >
              {physiciansList[0]?.group}
            </p>
            )}
          </div>

          <Button
            className={classes.searchButton}
            title="Cancel"
            type="button"
            href={pathname.replace('/access', '')}
            clearButton
          />
        </div>

        <div className={classes.search}>
          <InputField
            label="Search"
            value={name}
            onChange={onChangeSearch}
            placeholder="First or last name"
          />

          {loading && <Loader className={classes.loader_main} size={10} borderWidth={2} />}

          <Button
            className={classes.searchButton}
            title="Clear"
            type="button"
            onClick={clearInput}
            clearButton
          />
        </div>
      </div>

      {firstLoad
        ? <Loader className={classes.loader} />

        : (
          <div className={classes.main_wrapper}>
            <div>
              <p>Physician name</p>

              <p>Give access</p>
            </div>

            {physicianUsers.map((p) => {
              const showAllowBtn = group
                ? physiciansList.every((l) => p.physicians.find((ph) => +ph.id === +l.id))
                : health
                  ? healthList.some((l) => p.healthUser.find((ph) => +ph.id === +l.id))
                  : physiciansList.some((l) => p.physicians.find((ph) => +ph.id === +l.id));

              return (
                <div key={p.id}>
                  <p className={classes.user_name}>{p.username}</p>
                  {showAllowBtn
                    ? (
                      <Button
                        className={classes.accessButton}
                        title={(
                          <>
                            Access granted
                            <DeniedIcon className={classes.delete_icon} />
                          </>
                                  )}
                        type="button"
                        onClick={() => giveAccess(p.id)}
                        loading={buttonLoad.id === p.id ? buttonLoad.load : false}
                        buttonColor="#5F6C8E"
                      />
                    )
                    : (
                      <Button
                        className={classes.accessButton}
                        title="Give access"
                        type="button"
                        onClick={() => giveAccess(p.id, 'add')}
                        unlockButton
                        loading={buttonLoad.id === p.id ? buttonLoad.load : false}
                        reverseColor
                        buttonColor="#5F6C8E"
                      />
                    )}
                </div>
              );
            })}

            <Pagination
              onChange={onChangePageData}
              limit={+limit}
              page={+page - 1}
              totalPage={+physicianUsersTotalPage}
            />
          </div>
        )}
    </div>
  );
};

GiveAccess.propTypes = {
  group: PropTypes.bool,
  health: PropTypes.bool,
};

GiveAccess.defaultProps = {
  group: false,
  health: false,
};

export default GiveAccess;
